import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";

import { AppShell } from "@/components/app/app-shell";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { adminAuditLogQuery } from "@/lib/queries";

export const Route = createFileRoute("/_authenticated/t1dwpanel/audit")({
  head: () => ({
    meta: [
      { title: "Admin Audit Log | Warrior Volunteer" },
      {
        name: "description",
        content: "Record of every admin action taken in the T1D Warriors volunteer workspace.",
      },
      { property: "og:title", content: "Admin Audit Log | Warrior Volunteer" },
      {
        property: "og:description",
        content: "Record of every admin action taken in the T1D Warriors volunteer workspace.",
      },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: AdminAuditPage,
});

function formatWhen(value: string) {
  const date = new Date(value);
  return date.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function labelAction(action: string) {
  return action
    .replace(/^admin/, "")
    .replace(/([A-Z])/g, " $1")
    .trim();
}

function AdminAuditPage() {
  const { data: rows = [] } = useQuery(adminAuditLogQuery);
  const [search, setSearch] = useState("");

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((row) =>
      [row.action, row.actor_email, row.event_id, row.signup_id, row.target_id]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(q)),
    );
  }, [rows, search]);

  return (
    <AppShell eyebrow="Admin" title="Audit log">
      <div className="panel mb-4 flex flex-col gap-3 p-5 sm:flex-row sm:items-center sm:justify-between">
        <p className="t-meta">
          Every admin action in this panel, with who did it and what it touched. Mirrored to error
          reporting as breadcrumbs.
        </p>
        <Input
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search action, admin, or id"
          className="sm:max-w-xs"
        />
      </div>

      <div className="panel overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>When</TableHead>
              <TableHead>Who</TableHead>
              <TableHead>Action</TableHead>
              <TableHead>Related</TableHead>
              <TableHead>Details</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="py-10 text-center text-muted-foreground">
                  No admin actions recorded yet.
                </TableCell>
              </TableRow>
            ) : null}
            {filtered.map((row) => (
              <TableRow key={row.id}>
                <TableCell className="t-meta whitespace-nowrap">
                  {formatWhen(row.created_at)}
                </TableCell>
                <TableCell className="text-sm">
                  {row.actor_email ?? row.actor_id ?? "Unknown"}
                </TableCell>
                <TableCell className="text-sm font-medium">{labelAction(row.action)}</TableCell>
                <TableCell className="t-meta">
                  {row.event_id ? <div>Event {row.event_id.slice(0, 8)}</div> : null}
                  {row.signup_id ? <div>Signup {row.signup_id.slice(0, 8)}</div> : null}
                  {row.target_id ? <div>Record {row.target_id.slice(0, 8)}</div> : null}
                  {!row.event_id && !row.signup_id && !row.target_id ? "-" : null}
                </TableCell>
                <TableCell className="t-meta max-w-md truncate">
                  {row.details && Object.keys(row.details as object).length > 0
                    ? JSON.stringify(row.details)
                    : "-"}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </AppShell>
  );
}
